import { useEffect, useRef, CSSProperties } from 'react';
import { EventItem, EventOption } from '../../../types/Events';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Leaflet wymaga ręcznego wskazania ikon (CRA nie kopiuje ich automatycznie)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

interface EventMapProps {
    coords: string;
    label: string;
}

function EventMap({ coords, label }: EventMapProps) {
    const mapRef = useRef<HTMLDivElement>(null);
    const mapInstanceRef = useRef<L.Map | null>(null);

    useEffect(() => {
        if (!mapRef.current || mapInstanceRef.current) return;

        const [lat, lng] = coords.split(',').map(Number);
        if (isNaN(lat) || isNaN(lng)) return;

        const map = L.map(mapRef.current, {
            center: [lat, lng],
            zoom: 15,
            scrollWheelZoom: false,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(map);

        L.marker([lat, lng]).addTo(map).bindPopup(label);

        mapInstanceRef.current = map;

        return () => {
            map.remove();
            mapInstanceRef.current = null;
        };
    }, [coords, label]);

    return <div ref={mapRef} className="rejestracja__map" />;
}

// Limit miejsc jako osobna ramka na wpis, z zajętością pokazaną jako
// segmenty w barwach stref (spokojna → krytyczna) — granica wypełnienia
// sama jest znacznikiem, bez osobnej strzałki. Opcja bez twardego limitu
// (has_capacity_limit === false) dostaje stały, "nijaki" wzór zamiast
// segmentów — max_capacity to tam tylko wartość domyślna, nie realne
// ograniczenie, więc przypisanie stref nic by nie znaczyło.
//
// ZONE_COUNT steruje realnie WSZYSTKIM: liczbą segmentów, progami ich
// zapalania się I kolorami — kolor każdego segmentu jest interpolowany
// wzdłuż gradientu spokojna→krytyczna według jego pozycji, więc zmiana tej
// jednej stałej (np. na 4 albo 8) przelicza się na cały układ bez potrzeby
// ręcznego dopisywania nth-child w SCSS.
const ZONE_COUNT = 10;

// Stałe punkty gradientu (spokojna → krytyczna), pomiędzy którymi
// interpolowany jest kolor każdego segmentu.
const ZONE_GRADIENT_STOPS: Array<[number, number, number]> = [
    [10, 156, 71],   // zielony — spokojnie
    [124, 179, 66],  // żółto-zielony
    [242, 183, 5],   // żółty
    [232, 147, 10],  // pomarańczowy — ostrzegawczo
    [253, 50, 25],   // czerwony — krytycznie
];

function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t;
}

// Kolor segmentu "i" spośród "total" segmentów, interpolowany wzdłuż
// ZONE_GRADIENT_STOPS według jego względnej pozycji w rzędzie.
function zoneColor(index: number, total: number): string {
    const t = total <= 1 ? 1 : index / (total - 1);
    const scaled = t * (ZONE_GRADIENT_STOPS.length - 1);
    const i0 = Math.floor(scaled);
    const i1 = Math.min(ZONE_GRADIENT_STOPS.length - 1, i0 + 1);
    const localT = scaled - i0;

    const [r0, g0, b0] = ZONE_GRADIENT_STOPS[i0];
    const [r1, g1, b1] = ZONE_GRADIENT_STOPS[i1];

    const r = Math.round(lerp(r0, r1, localT));
    const g = Math.round(lerp(g0, g1, localT));
    const b = Math.round(lerp(b0, b1, localT));

    return `rgb(${r}, ${g}, ${b})`;
}

function CapacityCard({ opt }: { opt: EventOption }) {
    const unlimited = opt.has_capacity_limit === false;
    const pct = unlimited
        ? 100
        : Math.max(0, Math.min(100, (opt.current_count / Math.max(opt.max_capacity, 1)) * 100));
    // ceil, nie floor: segment ma się zapalić, gdy zajętość WESZŁA w jego
    // strefę (np. 44/45 = 97,8% musi zapalić strefę krytyczną), a nie
    // dopiero gdy strefa jest w 100% wyczerpana — floor wymagałby dosłownie
    // pełnego 100%, przez co ostatni segment praktycznie nigdy się nie zapalał.
    const filledZones = unlimited ? 0 : Math.min(ZONE_COUNT, Math.ceil(pct / (100 / ZONE_COUNT)));
    const label = unlimited
        ? `${opt.current_count} wybrało · bez limitu`
        : `${opt.current_count}/${opt.max_capacity} miejsc`;

    return (
        <div className="rejestracja__modal-optcard">
            <div className="rejestracja__modal-optcard-label">
                <b>{opt.option_name}</b> <span>— {label}</span>
            </div>
            <div className="rejestracja__modal-zonerow">
                {Array.from({ length: ZONE_COUNT }).map((_, i) => (
                    <div
                        key={i}
                        className={
                            'rejestracja__modal-zonecap' +
                            (unlimited
                                ? ' rejestracja__modal-zonecap--unlimited'
                                : i < filledZones
                                    ? ' rejestracja__modal-zonecap--filled'
                                    : '')
                        }
                        style={{ '--zc': zoneColor(i, ZONE_COUNT) } as CSSProperties}
                    />
                ))}
            </div>
        </div>
    );
}

export interface EventDetailsModalProps {
    event: EventItem | null;
    onClose: () => void;
    onGoToForm: (eventId: string) => void;
}

export function EventDetailsModal({ event, onClose, onGoToForm }: EventDetailsModalProps) {
    useEffect(() => {
        if (!event) return;

        document.body.style.overflow = 'hidden';

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = '';
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [event, onClose]);

    if (!event) return null;

    return (
        <div className="rejestracja__modal-backdrop" onClick={onClose}>
            <div
                className="rejestracja__modal"
                role="dialog"
                aria-modal="true"
                aria-label={`Szczegóły wydarzenia: ${event.title}`}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    className="rejestracja__modal-hero"
                    style={event.image ? { backgroundImage: `url(${event.image})` } : undefined}
                >
                    <button
                        type="button"
                        className="rejestracja__modal-close"
                        onClick={onClose}
                        aria-label="Zamknij"
                    >
                        ✕
                    </button>
                    <div className="rejestracja__modal-hero-body">
                        <div className="rejestracja__modal-status">
                            {event.registrations_open ? 'Zapisy otwarte' : 'Zapisy niedostępne'}
                        </div>
                        <h3 className="rejestracja__modal-title">{event.title}</h3>
                        {event.date && <div className="rejestracja__modal-date">{event.date}</div>}
                    </div>
                </div>

                <div className="rejestracja__modal-body">
                    {(event.duration || event.location_name) && (
                        <div className="rejestracja__modal-facts">
                            {event.duration && (
                                <div className="rejestracja__modal-fact">
                                    <b>{event.duration}</b>
                                    <span>Czas trwania</span>
                                </div>
                            )}
                            {event.location_name && (
                                <div className="rejestracja__modal-fact">
                                    <b>{event.location_name}</b>
                                    <span>Lokalizacja</span>
                                </div>
                            )}
                        </div>
                    )}

                    <p className="rejestracja__modal-desc">{event.description}</p>

                    {event.description_html && (
                        <div
                            className="rejestracja__modal-html"
                            dangerouslySetInnerHTML={{ __html: event.description_html }}
                        />
                    )}

                    {event.location_coords && event.location_name && (
                        <EventMap coords={event.location_coords} label={event.location_name} />
                    )}

                    {event.options && event.options.length > 0 && (
                        <div className="rejestracja__modal-manifest">
                            <div className="rejestracja__modal-manifest-title">Opcje</div>
                            <ul className="rejestracja__modal-plain-list">
                                {event.options.map((opt, i) => (
                                    <li key={i}>{opt}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {event.transport_options && event.transport_options.length > 0 && (
                        <div className="rejestracja__modal-manifest">
                            <div className="rejestracja__modal-manifest-title">🚌 Transport</div>
                            <div className="rejestracja__modal-optcard-list">
                                {event.transport_options.map((opt) => (
                                    <CapacityCard key={opt.id} opt={opt} />
                                ))}
                            </div>
                        </div>
                    )}

                    {event.hotel_options && event.hotel_options.length > 0 && (
                        <div className="rejestracja__modal-manifest">
                            <div className="rejestracja__modal-manifest-title">🏨 Nocleg</div>
                            <div className="rejestracja__modal-optcard-list">
                                {event.hotel_options.map((opt) => (
                                    <CapacityCard key={opt.id} opt={opt} />
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {event.registrations_open && (
                    <div className="rejestracja__modal-footer">
                        <button
                            type="button"
                            className="rejestracja__navigate-btn"
                            onClick={() => onGoToForm(event.id)}
                        >
                            <span className="rejestracja__navigate-btn-icon">⬇️</span>
                            <span className="rejestracja__navigate-btn-text">Przejdź do formularza</span>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
